import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Calendar,
  ChevronDown,
  Clock,
  MapPin,
  ShoppingCart,
  Ticket as TicketIcon,
} from "lucide-react";
import { API_BASE } from "@/lib/supabase";
import TicketStub from "@/components/site/TicketStub";
import WaitlistForm from "@/components/site/WaitlistForm";
import { formatPrice, formatEventDateTime } from "@/components/site/EventCard";
import { formatRegistrationDayDate } from "@/lib/registrationDays";
import RichText from "@/components/site/RichText";

function saleWindow(t) {
  const now = new Date();
  if (t.sale_starts_at && new Date(t.sale_starts_at) > now)
    return `Verkoop start ${new Date(t.sale_starts_at).toLocaleString("nl-NL")}`;
  if (t.sale_ends_at && new Date(t.sale_ends_at) < now) return "Verkoop gesloten";
  return null;
}

// Groups ticket types by their (optional) `category` for display, preserving the API's
// existing sort_order-based ordering both within and across groups. Returns null when no
// ticket type has a category at all, so callers can fall back to the old flat list —
// existing events without categories must render byte-identical to before.
function groupTicketTypes(types) {
  if (!types.some((t) => (t.category || "").trim())) return null;
  const order = [];
  const byCategory = new Map();
  const uncategorized = [];
  for (const t of types) {
    const cat = (t.category || "").trim();
    if (!cat) {
      uncategorized.push(t);
      continue;
    }
    if (!byCategory.has(cat)) {
      byCategory.set(cat, []);
      order.push(cat);
    }
    byCategory.get(cat).push(t);
  }
  return { uncategorized, sections: order.map((category) => ({ category, types: byCategory.get(category) })) };
}

function TicketTypeBlock({ t, ev, qtyByType, onQtyChange }) {
  const soldOut = t.capacity != null && (t.sold_count || 0) >= t.capacity;
  const reason = saleWindow(t);
  return (
    <div>
      <TicketStub
        type={t}
        qty={qtyByType[t.id] || 0}
        onChange={(n) => onQtyChange(t.id, n)}
        soldOut={soldOut}
        disabled={!!reason}
        reasonDisabled={reason}
        timeslotBooking={!!ev.timeslot_booking_enabled}
      />
      {soldOut && (
        <WaitlistForm eventId={ev.id} ticketTypeId={t.id} ticketTypeName={t.name} />
      )}
    </div>
  );
}

// Standard-open <details>: visitors see everything without an extra click, but can
// collapse a section once there are several (matches the reference layout).
function TicketSection({ title, children }) {
  return (
    <details
      open
      className="group"
      data-testid={`ticket-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-between py-1 select-none">
        <span className="text-sm font-bold uppercase tracking-wider text-white">{title}</span>
        <ChevronDown className="h-4 w-4 text-svj-cyan transition-transform duration-200 group-open:rotate-180" />
      </summary>
      <div className="mt-3 space-y-4">{children}</div>
    </details>
  );
}

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // "Terug"/"Alle events" used to always link to the generic /events overview,
  // even when the visitor actually arrived from an organisation's own page
  // (/organisatie/:slug) — reported directly: going back from an event lost
  // the organisation context entirely. react-router sets location.key to the
  // literal string "default" only for the very first entry in the session's
  // history (a fresh load/refresh/direct link with nothing to go back to);
  // any other value means this page was reached by navigating within the app,
  // so history actually has somewhere real to return to.
  const canGoBackInApp = location.key !== "default";
  const goBack = () => {
    if (canGoBackInApp) navigate(-1);
    else navigate("/events");
  };
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qtyByType, setQtyByType] = useState({});
  const [registrationDays, setRegistrationDays] = useState([]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/events/${encodeURIComponent(slug)}`);
        if (!ignore) setEv(data);
      } catch (e) {
        if (!ignore) setError(e?.response?.status === 404 ? "Event niet gevonden" : e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [slug]);

  // Multi-day registration ("Lichtjestour"-style) events span several separate
  // dates/times rather than one continuous run -- the header's Van/Tot line above
  // only shows the event's overall outer window (e.g. 10-19 september), which
  // doesn't tell a visitor which of those evenings are actually bookable. Fetches
  // the same public, read-only endpoint Checkout.jsx already uses for its day
  // picker, so a visitor can see the real per-day dates before starting checkout.
  useEffect(() => {
    if (!ev?.multi_day_registration_enabled || !ev?.id) {
      setRegistrationDays([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/events/${ev.id}/days`);
        if (!ignore) setRegistrationDays(data?.items || []);
      } catch {
        if (!ignore) setRegistrationDays([]);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [ev?.multi_day_registration_enabled, ev?.id]);

  const totalCents = useMemo(() => {
    if (!ev) return 0;
    return (ev.ticket_types || []).reduce(
      (acc, t) => acc + (qtyByType[t.id] || 0) * (t.price_cents || 0),
      0,
    );
  }, [ev, qtyByType]);

  const totalQty = useMemo(
    () => Object.values(qtyByType).reduce((a, b) => a + (b || 0), 0),
    [qtyByType],
  );

  // Mirrors Checkout.jsx's own allPayOnLocation check: only when every relevant
  // ticket type opted in does the flow skip online payment. Before anything is
  // selected there's nothing to check against the actual selection yet, so this
  // falls back to every ticket type the event actually offers — an event whose
  // only ticket type is pay-on-location (or free) should read that way from the
  // very first render, not only after the visitor bumps the quantity to 1.
  const relevantTypes = useMemo(() => {
    const types = ev?.ticket_types || [];
    if (totalQty > 0) return types.filter((t) => (qtyByType[t.id] || 0) > 0);
    return types;
  }, [ev, qtyByType, totalQty]);
  // A timeslot-booking event's ticket type price_cents is a meaningless
  // placeholder here -- the real, per-ticket-unit price only exists once a
  // category/timeslot is chosen during checkout, so this page must never
  // claim "Gratis"/pay-on-location from it (that claim was only ever true by
  // accident of whatever placeholder value the ticket type happened to have).
  const isTimeslotBooking = !!ev?.timeslot_booking_enabled;
  const allSelectedPayOnLocation =
    !isTimeslotBooking && relevantTypes.length > 0 && relevantTypes.every((t) => t.pay_on_location);
  const isFreeSelection =
    !isTimeslotBooking && relevantTypes.length > 0 && relevantTypes.every((t) => (t.price_cents || 0) === 0);
  const skipsOnlinePayment = allSelectedPayOnLocation || isFreeSelection;

  const grouped = useMemo(() => groupTicketTypes(ev?.ticket_types || []), [ev]);

  const onQtyChange = (typeId, next) => {
    setQtyByType((prev) => ({ ...prev, [typeId]: Math.max(0, next) }));
  };

  const goCheckout = () => {
    if (!ev || totalQty === 0) {
      toast.error("Selecteer eerst minimaal één ticket");
      return;
    }
    const draft = {
      event_id: ev.id,
      event_slug: ev.slug,
      event_title: ev.title,
      companion_app_enabled: !!ev.companion_app_enabled,
      multi_day_registration_enabled: !!ev.multi_day_registration_enabled,
      timeslot_booking_enabled: !!ev.timeslot_booking_enabled,
      checkout_hide_address_fields: !!ev.checkout_hide_address_fields,
      participant_terms_enabled: !!ev.participant_terms_enabled,
      participant_terms_url: ev.participant_terms_url || "",
      organizer_terms_enabled: !!ev.organizer_terms_enabled,
      organizer_terms_text: ev.organizer_terms_text || "",
      items: (ev.ticket_types || [])
        .map((t) => ({
          ticket_type_id: t.id,
          ticket_name: t.name,
          qty: qtyByType[t.id] || 0,
          unit_price_cents: t.price_cents,
          vat_rate: t.vat_rate,
          pay_on_location: !!t.pay_on_location,
        }))
        .filter((i) => i.qty > 0),
    };
    sessionStorage.setItem("svj_order_draft", JSON.stringify(draft));
    navigate("/checkout");
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="animate-pulse space-y-6">
          <div className="h-80 bg-svj-card rounded-2xl" />
          <div className="h-8 w-2/3 bg-svj-card rounded" />
          <div className="h-4 w-1/3 bg-svj-card rounded" />
        </div>
      </div>
    );
  }

  if (error || !ev) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="text-white text-xl font-semibold" data-testid="event-detail-error">
          {error || "Event niet gevonden"}
        </div>
        <button
          type="button"
          onClick={goBack}
          className="mt-6 inline-flex items-center gap-2 text-svj-cyan hover:text-white font-semibold"
        >
          <ArrowLeft className="h-4 w-4" /> Terug naar events
        </button>
      </div>
    );
  }

  const org = ev.organization || {};
  const allSoldOut =
    (ev.ticket_types || []).length > 0 &&
    (ev.ticket_types || []).every((t) => t.capacity != null && (t.sold_count || 0) >= t.capacity);

  return (
    <div data-testid="event-detail-page">
      {/* Cover */}
      <section className="relative">
        <div className="relative w-full aspect-[16/6] max-h-[520px] overflow-hidden">
          <img
            src={ev.cover_image_url}
            alt={ev.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-svj-bg/50 via-svj-bg/70 to-svj-bg" />
          <div className="absolute top-6 left-6">
            <div className="bg-white p-1 rounded-full shadow-lg w-14 h-14 flex items-center justify-center overflow-hidden">
              {org.logo_url ? (
                <img src={org.logo_url} alt={org.name} className="w-full h-full object-cover rounded-full" />
              ) : (
                <Building2 className="h-7 w-7 text-svj-purple" />
              )}
            </div>
          </div>
          {allSoldOut && (
            <div className="absolute top-6 right-6 bg-black/70 backdrop-blur-md border border-svj-border text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
              Uitverkocht
            </div>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24 relative z-10">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-4"
          data-testid="event-detail-back"
        >
          <ArrowLeft className="h-4 w-4" /> Alle events
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left column */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">
              {org.slug && org.name ? (
                <Link to={`/organisatie/${org.slug}`} className="hover:text-white transition-colors">
                  {org.name}
                </Link>
              ) : (
                org.name || "Event"
              )}
            </div>
            <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
              {ev.title}
            </h1>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-300">
              {/* A multi-day registration event's own starts_at/ends_at is just the
                  outer window (e.g. "10 september" tot "19 september") -- meaningless
                  on its own and, now that "Beschikbare data" below shows the real
                  per-day dates/times, genuinely duplicative. Mirrors the backend's own
                  header_date_line convention (server.py) for the PDF ticket/email,
                  which already omits this same generic date for the same event type. */}
              {!ev.multi_day_registration_enabled && (
                <span className="inline-flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-svj-cyan" />
                  {formatEventDateTime(ev.starts_at)}
                </span>
              )}
              {!ev.multi_day_registration_enabled && ev.ends_at && (
                <span className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4 text-svj-cyan" />
                  Tot {formatEventDateTime(ev.ends_at)}
                </span>
              )}
              {ev.location && (
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-svj-cyan" />
                  {ev.location}
                </span>
              )}
            </div>

            {ev.multi_day_registration_enabled && registrationDays.length > 0 && (
              <div className="mt-4" data-testid="event-detail-registration-days">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Beschikbare data
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {registrationDays.map((d) => (
                    <span
                      key={d.id}
                      data-testid={`event-detail-registration-day-${d.id}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${
                        d.full
                          ? "border-svj-border bg-svj-card text-slate-500"
                          : "border-svj-border bg-svj-card text-slate-300"
                      }`}
                    >
                      <Calendar className="h-3.5 w-3.5 text-svj-cyan" />
                      {d.label ? `${d.label} — ` : ""}
                      {formatRegistrationDayDate(d)}
                      {d.full && (
                        <span className="ml-1 text-[10px] uppercase tracking-widest text-red-400">Vol</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {ev.description && (
              <div
                className="mt-8 bg-svj-card border border-svj-border rounded-2xl p-6 text-slate-300 whitespace-pre-line leading-relaxed"
                data-testid="event-description"
              >
                <RichText text={ev.description} />
              </div>
            )}
          </div>

          {/* Right sticky column: ticket types + checkout */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-4" data-testid="ticket-types-list">
              <div className="flex items-center gap-2">
                <TicketIcon className="h-4 w-4 text-svj-cyan" />
                <div className="text-sm font-semibold text-white uppercase tracking-wider">
                  Tickets
                </div>
              </div>
              {(ev.ticket_types || []).length === 0 && (
                <div className="text-sm text-slate-400" data-testid="ticket-empty">
                  Er zijn nog geen tickets beschikbaar.
                </div>
              )}
              {/* No ticket type has a category: identical flat list to before (no regression
                  for existing events). Only once categories are actually in use do we group
                  into standard-open, collapsible sections. */}
              {!grouped &&
                (ev.ticket_types || []).map((t) => (
                  <TicketTypeBlock key={t.id} t={t} ev={ev} qtyByType={qtyByType} onQtyChange={onQtyChange} />
                ))}
              {grouped && (
                <>
                  {grouped.uncategorized.map((t) => (
                    <TicketTypeBlock key={t.id} t={t} ev={ev} qtyByType={qtyByType} onQtyChange={onQtyChange} />
                  ))}
                  {grouped.sections.map((sec) => (
                    <TicketSection key={sec.category} title={sec.category}>
                      {sec.types.map((t) => (
                        <TicketTypeBlock key={t.id} t={t} ev={ev} qtyByType={qtyByType} onQtyChange={onQtyChange} />
                      ))}
                    </TicketSection>
                  ))}
                </>
              )}

              <div className="bg-svj-card border border-svj-border rounded-2xl p-5">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-300">Totaal ({totalQty} tickets)</div>
                  <div
                    className="text-2xl font-extrabold text-svj-cyan"
                    data-testid="event-total-amount"
                  >
                    {isTimeslotBooking ? "Per afspraak" : isFreeSelection ? "Gratis" : formatPrice(totalCents)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={goCheckout}
                  disabled={totalQty === 0}
                  data-testid="event-goto-checkout"
                  className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-3 px-6 font-bold transition-all ${
                    totalQty > 0
                      ? "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white hover:shadow-[0_0_24px_rgba(214,61,122,0.5)]"
                      : "bg-svj-bg/60 border border-svj-border text-slate-500 cursor-not-allowed"
                  }`}
                >
                  <ShoppingCart className="h-5 w-5" />
                  {skipsOnlinePayment ? "Aanmelden" : "Naar checkout"}
                </button>
                {skipsOnlinePayment ? (
                  <div
                    className="mt-3 text-[11px] text-svj-cyan text-center"
                    data-testid="event-pay-on-location-note"
                  >
                    {allSelectedPayOnLocation
                      ? "Je betaalt ter plekke bij het evenement, niet nu online."
                      : "Gratis — geen betaling nodig."}
                  </div>
                ) : (
                  <div className="mt-3 text-[11px] text-slate-500 text-center">
                    Veilig betalen via iDEAL en andere methoden.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
